import { FormControl, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FieldBridge from "../FieldBridge";
import MuiSelect from "./MuiSelect";
import MuiMenuItem from "./MuiMenuItem";
import { StyledInputField, StyledTextField } from "customStyled";
import { withTrimHandler } from "utils/reactiveFormFunc";
import FormLabel from "../FormLabel";
import { useMemo } from "react";
import { autocompleteToken } from "utils";
import { placeholderOption, placeholderText, selectText } from "./selectPlaceholder";

function SelectUI({
  id,
  autoComplete,
  placeholder,
  optionList,
  sx = {},
  multiple = false,
  variant = "outlined",
  label,
  required,
  ariaLabel,
}) {
  const placeHolder = placeholder ? selectText(placeholder) : selectText();
  const optionLists = useMemo(
    () => (Array.isArray(optionList) ? optionList : []),
    [optionList],
  );
    
  // ✅ 1. Create map for fast lookup
    const valueLabelMap = useMemo(() => {
      const map = new Map();
      optionLists.forEach((opt) => {
        map.set(opt.value, opt.label);
      });
      return map;
    }, [optionLists]);
  
    // ✅ 2. Centralized placeholder
    const renderPlaceholder = () => (
      <Typography sx={(theme) => ({ color: theme.palette.grey[500] })}>
        {placeHolder}
      </Typography>
    );
  
    // ✅ 3. Clean renderValue
    const renderValue = (selected) => {
      // 🔑 Always check map FIRST
      if (!multiple) {
        return valueLabelMap.get(selected) ?? renderPlaceholder();
      }
  
      // multiple
      if (!Array.isArray(selected) || selected.length === 0) {
        return renderPlaceholder();
      }
  
      const labels = selected
        .map((val) => valueLabelMap.get(val))
        .filter(Boolean);
  
      return labels.length ? labels.join(", ") : renderPlaceholder();
    };
  return (
    <FieldBridge
      name={id}
      render={({ handler, pending, touched, invalid }) => {
        const isError = touched && invalid;
        return (
          <FormControl error={isError}>
            <FormLabel label={label} required={required} id={id} />
            <MuiSelect
              id={id}
              name={id}
              IconComponent={KeyboardArrowDownIcon}
              // named by its label; without one, by ariaLabel or the placeholder
              labelId={label ? `${id}-label` : undefined}
              inputProps={label ? undefined : { "aria-label": ariaLabel || placeholder || id }}
              autoComplete={autocompleteToken(autoComplete)}
              {...withTrimHandler(handler)}
              displayEmpty
              variant={variant}
              input={
                variant === "outlined" ? (
                  <StyledTextField />
                ) : (
                  <StyledInputField />
                )
              }
              multiple={multiple}
              renderValue={renderValue}
              sx={{
                height: 44,
                ...sx,
              }}
              aria-describedby={`${id}-helper-text`}
            >
              {/* the first, disabled option: the placeholder, or "no options" (not when
                  an option already stands for "nothing chosen", e.g. "All Users") */}
              {optionLists.some((o) => o.value === "")
                ? null
                : placeholderOption(placeholderText({ placeholder: placeHolder, hasOptions: optionLists.length > 0 }))}
              {optionLists.map((option, index) => (
                <MuiMenuItem key={`${option.value}-${index}`} value={option.value}>
                  {option.label}
                </MuiMenuItem>
              ))}
            </MuiSelect>
          </FormControl>
        );
      }}
    />
  );
}

export default SelectUI;
